import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    useTheme
} from "@mui/material";
import {MouseEventHandler, ReactNode, useEffect, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import {useTranslation} from "react-i18next";

export type DialogTitleProps = {
    id: string;
    children?: ReactNode;
    onClose: () => void;
}

const DialogComponent = ({open, setOpen, onClickConfirm, type, message}: {
    open: boolean,
    setOpen: Function,
    onClickConfirm: MouseEventHandler<HTMLButtonElement>,
    type: string,
    message: string,
}) => {

    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use State
    const [content, setContent] = useState<any>({icon: null, title: "", actions: null});

    const BootstrapDialogTitle = (props: DialogTitleProps) => {
        const {children, onClose, ...other} = props;

        return (
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: theme.palette.primary.main
                }} {...other} >
                {children}
                {onClose ? (
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: (theme) => theme.palette.grey[500]
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>
                ) : null}
            </DialogTitle>
        );
    };

    useEffect(() => {
        switch (type) {
            case "info": {
                setContent({
                    icon: <InfoRoundedIcon sx={{color: theme.palette.accent.main}}/>,
                    title: t("pricing:dialogs.titles.info"),
                    content: <>
                        <Typography sx={{lineHeight: 2.5}}>{message}</Typography>
                        <Typography
                            sx={{lineHeight: 2.5}}>{t("pricing:dialogs.messages.doYouConfirm")}</Typography>
                    </>,
                    actions: <>
                        <Button variant="outlined"
                                color={"accent"}
                                onClick={() => setOpen(false)}
                        >
                            {t("pricing:actions.cancel")}
                        </Button>
                        <Button sx={{marginLeft: "16px"}}
                                autoFocus
                                color={"accent"}
                                onClick={onClickConfirm}
                        >
                            {t("pricing:actions.proceed")}
                        </Button>
                    </>
                });
                break;
            }
            case "success": {
                setContent({
                    icon: <CheckCircleRoundedIcon color={"success"}/>,
                    title: t("pricing:dialogs.titles.success"),
                    content: <Typography sx={{lineHeight: 2.5}}>{message}</Typography>,
                    actions: <Button color={"accent"}
                                     onClick={() => setOpen(false)}
                    >
                        {t("pricing:actions.close")}
                    </Button>
                });
                break;
            }
            case "error": {
                setContent({
                    icon: <WarningRoundedIcon color={"error"}/>,
                    title: t("pricing:dialogs.titles.error"),
                    content: <Typography sx={{lineHeight: 2.5}}>{message}</Typography>,
                    actions: <Button color={"accent"}
                                     onClick={() => setOpen(false)}
                    >
                        {t("pricing:actions.close")}
                    </Button>
                });
                break;
            }
        }
        // eslint-disable-next-line
    }, [type]);

    return (
        <Dialog open={open} maxWidth={"sm"} fullWidth>
            <BootstrapDialogTitle id="customized-dialog-title" onClose={() => setOpen(!open)}>
                <Box sx={{display: "flex", alignItems: "center"}}>
                    {content.icon} <span style={{marginLeft: 8}}>{content.title}</span>
                </Box>
            </BootstrapDialogTitle>
            <DialogContent style={{color: theme.palette.primary.main}}>
                {content.content}
            </DialogContent>
            <DialogActions style={{backgroundColor: theme.palette.neutral.bright, padding: "1rem"}}>
                {content.actions}
            </DialogActions>
        </Dialog>
    );
};

export default DialogComponent;